import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { trim } from 'lodash';

type ReturnedType = any;

interface TripBooking {
  adultCount: number;
  agentName: string;
  childCount: number;
  date: string | null;
  destination: string | null;
  endLat: number | null;
  endLng: number | null;
  bufferTime: string | null;
  guestName: string;
  mode: string;
  refNumber: string;
  tourName: string | null;
  source: string | null;
  startLat: number | null;
  startLng: number | null;
  startTimestamp: number | null;
}

interface UpdateTripBooking {
  adultCount: number;
  agentName: string;
  childCount: number;
  date: string | null;
  destination: string | null;
  endLat: number | null;
  endLng: number | null;
  bufferTime: string | null;
  guestName: string;
  mode: string;
  refNumber: string;
  tourName: string | null;
  source: string | null;
  startLat: number | null;
  startLng: number | null;
  // startTimestamp: number | null | undefined;
}

interface Payload {
  pageNo: number | null;
  pageSize: number | null;
  groupId?: string;
  sortByField?: string;
  sortBy?: string;
}

interface TripWithPage {
  payload: TripBooking;
  pageDetails: { pageNo: number; pageSize: number };
}

interface TripUpdateWithPage {
  payload: UpdateTripBooking;
  pageDetails: { pageNo: number; pageSize: number };
  selectedRow: any;
}

interface ImportTrip {
  file: File | null;
  invalidRows: any;
  isReUpload: any;
}

// Define the structure of the data returned by the API
interface AutoGeneratedTripsViewList {
  count: number;
  groupedTrips: any;
  passengerJourneyInfoList: any;
  customTours: any;
}

interface AutoGeneratedStatus {
  status: string;
  data: AutoGeneratedTripsViewList | null;
  message: string;
}

// Define the shape of the Redux state
interface AutoGeneratedTripState {
  isLoading: boolean;
  data: AutoGeneratedStatus | null;
  error: string | null;
}

const initialState: AutoGeneratedTripState = {
  isLoading: false,
  data: null,
  error: null
};

const initialStateDropDown = {
  isLoading: false,
  data: null,
  error: null
};

const initialStates = {
  isLoading: false,
  data: [],
  error: null
};

// get agent details
export const autoPlannerAgentAction = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplanneragent',
  async (signal, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_AUTO_PLANNERTRIP}s/agent`,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

// get routes details
export const autoPlannerRoutesAction = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplannerroutes',
  async (data, thunkApi) => {
    const { mode, currentSecond } = data;
    try {
      let url = `${URLs.GET_AUTO_PLANNERTRIP}s/tour?mode=${mode}`;

      if (currentSecond) {
        url += `&currentSecond=${currentSecond}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get trip details
export const autoPlannerTripsAction = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplanneruser/trips',
  async (params, thunkApi) => {
    const {
      pageNo,
      pageSize,
      agentname,
      route,
      triptype,
      autoplannerID,
      sortBy,
      sortByField,
      search,
      signal
    } = params;

    const queryParams: Record<string, string | number | boolean | undefined> = {
      pageNo,
      pageSize,
      agentName: agentname,
      routeName: route,
      tripType: triptype,
      autoplannerID,
      sortBy,
      sortByField,
      searchFor: search
    };

    const filteredParams = Object.entries(queryParams)
      .filter(
        ([key, value]) =>
          value !== undefined && value !== null && value !== '' && !Number.isNaN(value)
      )
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_SIC_TSIC_TRIP}mode=${params.tripMode}&${filteredParams}`,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

// get trip details
export const autoPlannerBookingAction = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplanner booking/trips',
  async (params, thunkApi) => {
    const { pageNo, pageSize, tourName, mode, data, agentName } = params;
    try {
      let url = `${URLs.GET_BOOKINGS_TRIP}tourName=${encodeURIComponent(
        tourName
      )}&mode=${mode}&date=${data}&pageNo=${pageNo}&pageSize=${pageSize}`;
      if (agentName) {
        url += `&agentName=${agentName}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get GRP trip details

export const GetGrpBookings = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplanner booking/trips',
  async (params, thunkApi) => {
    const {
      pageNo,
      pageSize,
      agentname,
      route,
      triptype,
      autoplannerID,
      sortBy,
      sortByField,
      guestName,
      source,
      destination,
      search,
      signal,
      mode,
      isAdhoc = false
    } = params;

    const queryParams: Record<string, string | number | boolean | undefined> = {
      pageNo,
      pageSize,
      agentName: agentname,
      routeName: route,
      tripType: triptype,
      source: trim(source),
      destination: trim(destination),
      guestName: guestName,
      autoplannerID,
      sortBy,
      sortByField,
      searchFor: search,
      isAdhoc
    };

    const filteredParams = Object.entries(queryParams)
      .filter(
        ([key, value]) =>
          value !== undefined && value !== null && value !== '' && !Number.isNaN(value)
      )
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    try {
      if (autoplannerID) {
        const response = await axiosInstance({
          method: 'GET',
          url: `${URLs.GET_GRP_BOOKINGS}mode=${
            mode ? mode : params.tripMode
          }&${filteredParams}`,
          signal: signal
        });
        return response?.data;
      }
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

// all bookings get
export const GetAllBookings = createAsyncThunk<AxiosResponse<any>, any>(
  'autoplanner all booking/trips',
  async (params, thunkApi) => {
    const {
      pageNo,
      pageSize,
      agentname,
      route,
      triptype,
      autoplannerID,
      sortBy,
      sortByField,
      guestName,
      source,
      destination,
      search,
      signal,
      mode
    } = params;

    const queryParams: Record<string, string | number | boolean | undefined> = {
      pageNo,
      pageSize,
      agentName: agentname,
      routeName: route,
      tripType: triptype,
      source: trim(source),
      destination: trim(destination),
      guestName: guestName,
      sortBy,
      mode,
      sortByField,
      searchFor: search
    };

    const filteredParams = Object.entries(queryParams)
      .filter(
        ([key, value]) =>
          value !== undefined && value !== null && value !== '' && !Number.isNaN(value)
      )
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    try {
      if (autoplannerID) {
        const response = await axiosInstance({
          method: 'GET',
          url: `${URLs.GET_ALL_BOOKINGS}date=${params.autoplannerID}&${filteredParams}`,
          signal: signal
        });
        return response?.data;
      }
    } catch (error: any) {
      if (error?.message !== 'canceled') return thunkApi.rejectWithValue(error);
    }
  }
);

//action for add trip
export const autoPlannerAddTripsAction = createAsyncThunk<ReturnedType, any>(
  'autoplannertripadd/trips',
  async (data, thunkApi) => {
    const { APagent, APsubAgent } = data;
    try {
      let url = `${URLs.ADD_AUTO_PLANNERTRIP}`;
      if (APagent || APsubAgent) {
        url += `?isAdhoc=true`;
      }
      const response = await axiosInstance({
        method: 'POST',
        url: url,
        data: data.payload
      });
      !APagent &&
        !APsubAgent &&
        (await thunkApi.dispatch(
          data.pageDetails.isAll
            ? GetGrpBookings({
                pageNo: data?.pageDetails.pageNo,
                pageSize: data?.pageDetails.pageSize,
                autoplannerID: data?.pageDetails?.autoplannerID,
                search: data?.pageDetails?.search,
                tripMode: 'ALL'
              })
            : autoPlannerTripsAction({ ...data.pageDetails, tripMode: data.payload.mode })
        ));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data[0];
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error.response.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to update trip

export const autoPlannerUpdateTripsAction = createAsyncThunk<ReturnedType, any>(
  'updateautoplennertrip/trips',
  async (data, thunkApi) => {
    const { APagent, APsubAgent } = data;
    const { isAdhoc } = data?.selectedRow;
    const isTour =
      (data?.selectedRow.mode === 'SIC' || data?.selectedRow.mode === 'TSIC') &&
      !data.pageDetails?.isAll;
    try {
      let url = `${URLs.UPDATE_AUTO_PLANNERTRIP}`;
      if ((APagent || APsubAgent) && isAdhoc === true) {
        url += `?isAdhoc=true`;
      }
      const response = await axiosInstance({
        method: 'PUT',
        url: url,
        data: data.payload
      });
      {
        !isAdhoc &&
          (await thunkApi.dispatch(
            isTour
              ? autoPlannerBookingAction({
                  pageNo: data?.pageDetails?.pageNo,
                  pageSize: data?.pageDetails?.pageSize,
                  mode: data?.selectedRow.mode,
                   tourName: data?.selectedRow.tourName,
                  data: data?.selectedRow.date
                })
              : GetGrpBookings({
                  pageNo: data?.pageDetails?.pageNo,
                  pageSize: data?.pageDetails?.pageSize,
                  autoplannerID: data?.pageDetails?.autoplannerID,
                  tripMode: data.pageDetails.isAll ? 'ALL' : data?.selectedRow.mode,
                  search: data?.pageDetails?.search
                })
          ));
      }
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data[0];
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error
            ? error.response.data?.error
            : error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to delete trip

export const autoPlannerDeleteAction = createAsyncThunk<ReturnedType, any>(
  'auto-planner-trip/delete',
  async (data, thunkApi) => {
    const isTour = data?.selectedRow.mode === 'SIC' || data?.selectedRow.mode === 'TSIC';
    const { isAdhoc } = data?.selectedRow;
    const { APagent, APsubAgent } = data;
    const isadhoc = (APagent || APsubAgent) && isAdhoc === true;
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_AUTO_PLANNERTRIP}tripId=${
          data?.selectedRow?.trip_id || data?.selectedRow?.tripID
        }&journeyID=${
          data?.selectedRow?.journey_id || data?.selectedRow?.journeyId
        }&isAdhoc=${isadhoc || false}`
      });

      await thunkApi.dispatch(
        isTour
          ? autoPlannerBookingAction({
              pageNo: data?.pageDetails.pageNo,
              pageSize: data?.pageDetails.pageSize,
              mode: data?.selectedRow.mode,
              tourName: data?.selectedRow.tourName,
              data: data?.selectedRow.date
            })
          : data.isAll
          ? GetAllBookings({
              pageNo: data?.pageDetails.pageNo,
              pageSize: data?.pageDetails.pageSize,
              autoplannerID: data?.pageDetails?.autoplannerID,
              tripMode: data?.selectedRow.mode,
              search: data?.pageDetails?.search
            })
          : GetGrpBookings({
              pageNo: data?.pageDetails.pageNo,
              pageSize: data?.pageDetails.pageSize,
              autoplannerID: data?.pageDetails?.autoplannerID,
              tripMode: data?.mode,
              search: data?.pageDetails?.search
            })
      );
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error
            ? error.response.data?.error
            : error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// import file to upload bulk trips
export const autoPlannerImportTripsAction = createAsyncThunk<ReturnedType, ImportTrip>(
  'autoplanner/importTrips',
  async (data, thunkApi) => {
    try {
      const formData = new FormData();
      if (data.file) {
        formData.append('file', data.file);
      }

      const response = await axiosInstance.post(
        `${URLs.IMPORT_AUTO_PLANNER_TRIPS}?isReUpload=${data.isReUpload}&skippingRows=${data.invalidRows}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response?.data?.data;
    } catch (error: any) {
      if (error?.response?.data?.message)
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message,
            severity: 'error'
          })
        );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//action for update main pickup trip
export const newPickupUpdateAction = createAsyncThunk<ReturnedType, any>(
  'new pickup location/trips',
  async (data, thunkApi) => {
    const formData = new FormData();
    if (data.file) {
      formData.append('file', data.file);
    }
    if (data.importPickupHotels) {
      formData.append('importPickupHotels', JSON.stringify(data.importPickupHotels));
    }
    try {
      const response = await axiosInstance.post(
        `${URLs.NEW_PICKUP_LOCATION_FIND}?skippingRows=${data.skippings}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message || 'Internal Server Error',
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message || 'Internal Server Error',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// auto planner agent details slice
const autoPlannerAgent = createSlice({
  name: 'autoplanneragent',
  initialState: initialStateDropDown,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerAgentAction.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        autoPlannerAgentAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(autoPlannerAgentAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// auto planner route details slice
const autoPlannerRoutes = createSlice({
  name: 'autoplannerroutes',
  initialState: initialStateDropDown,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerRoutesAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerRoutesAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.error = null;
        }
      )
      .addCase(autoPlannerRoutesAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// auto planner trips details slice
const autoPlannerTripsSlice = createSlice({
  name: 'autoplannertrips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        autoPlannerTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;

          state.error = null;
        }
      )
      .addCase(autoPlannerTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// auto planner booking details slice
const autoPlannerBookingSlice = createSlice({
  name: 'autoplannerbooking',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerBookingAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerBookingAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(autoPlannerBookingAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// auto planner booking details slice
const grpbookingSlice = createSlice({
  name: 'autoplannerbooking',
  initialState,
  reducers: {
    clearBookings: (state: any) => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(GetGrpBookings.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(GetGrpBookings.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(GetGrpBookings.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// auto planner booking details slice
const allbookingSlice = createSlice({
  name: 'allautoplannerbooking',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(GetAllBookings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(GetAllBookings.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(GetAllBookings.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

// Add trips slice
const addAutoPlannerTrip = createSlice({
  name: 'add auto planner trips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerAddTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerAddTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(autoPlannerAddTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

// Update auto trips slice
const upadateAutoPlannerTrip = createSlice({
  name: 'auto planner trips update',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerUpdateTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerUpdateTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(autoPlannerUpdateTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

// delete auto planner trips slice
const deleteAutoPlannerSlice = createSlice({
  name: 'auto planner trips delete',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(autoPlannerDeleteAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerDeleteAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(autoPlannerDeleteAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

// import trips slice
const importAutoPlannerTrip = createSlice({
  name: 'import auto planner trips',
  initialState: initialStates,
  reducers: {
    clearNewPickup: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(autoPlannerImportTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerImportTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(autoPlannerImportTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload?.response?.data?.data;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

//new pickup location slice
const newPickupLocationSlice = createSlice({
  name: 'new pickup update',
  initialState: initialStates,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(newPickupUpdateAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        autoPlannerBookingAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(autoPlannerBookingAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

export const contactNumberValidation = createAsyncThunk<any, any>(
  'contact/validation',
  async (data, thunkAPi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.CONATCT_VALIDATION}&value=${encodeURIComponent(data)}`
      });

      return response;
    } catch (error: any) {
      return error?.response?.data?.message;
    }
  }
);

const conatctValidationSlice = createSlice({
  name: 'contactValidation',
  initialState,
  reducers: {
    clearContactError: (state: any) => {
      state.data = null;
    }
    // setValidationErrors: (state, action) => {
    //   if (action.payload.fieldName === 'contactPhone')
    //     state.contactPhone = action.payload;
    // }
  },
  extraReducers: builder => {
    builder.addCase(contactNumberValidation.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(contactNumberValidation.fulfilled, (state, action: any) => {
      state.isLoading = false;
      state.data = action?.payload;
      state.error = null;
    });
    builder.addCase(contactNumberValidation.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

//adhoc vehicle dropdown
export const getAdhocVehicle = createAsyncThunk<AxiosResponse<any>, any>(
  'get adhoc vehicle',
  async (params, thunkApi) => {
    const { date, data } = params;
    try {
      let url = `${URLs.ADHOC_VEHICLE_DROPDOWN}?date=${date}`;
      const response = await axiosInstance({
        method: 'POST',
        url: url,
        data: data
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// adhoc vehicle dropdown slice
const getAdhocVehicleSlice = createSlice({
  name: 'getadhocvehicle',
  initialState,
  reducers: {
    clearAdhocVehicle: (state: any) => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAdhocVehicle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        getAdhocVehicle.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(getAdhocVehicle.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//adhoc transfer vehicle dropdown
export const getAdhocTransferVehicle = createAsyncThunk<AxiosResponse<any>, any>(
  'get adhoc transfer vehicle',
  async (params, thunkApi) => {
    const { date, data } = params;
    try {
      let url = `${URLs.ADHOC_TRANSFER_VEHICLES}?date=${date}`;
      const response = await axiosInstance({
        method: 'POST',
        url: url,
        data: data
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

//adhoc transfer vehicle dropdown slice
const getAdhocTransferVehicleSlice = createSlice({
  name: 'getadhoctransfervehicle',
  initialState,
  reducers: {
    clearAdhocTransferVehicle: (state: any) => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAdhocTransferVehicle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        getAdhocTransferVehicle.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(getAdhocTransferVehicle.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//add adhoc vehicle //
export const AddAdhocVehicle = createAsyncThunk<AxiosResponse<any>, any>(
  'add adhoc vehicle',
  async (params, thunkApi) => {
    const { date, data, method, journeyID } = params;

    try {
      let url =
        method === 'PUT'
          ? `${URLs.ADD_ADHOC_VEHICLE}?date=${date}&journeyID=${journeyID}`
          : `${URLs.ADD_ADHOC_VEHICLE}?date=${date}`;
      const response = await axiosInstance({
        method: method,
        url: url,
        data: data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error.response.message,
          severity: 'error'
        })
      );
    }
  }
);

// adhoc vehicle slice
const AddAdhocVehicleSlice = createSlice({
  name: 'addadhocvehicle',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(AddAdhocVehicle.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        AddAdhocVehicle.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(AddAdhocVehicle.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

//update adhoc vehicle
// export const updateAdhocVehicle = createAsyncThunk<AxiosResponse<any>, any>(
//   'update adhoc vehicle',
//   async (params, thunkApi) => {
//     const { date, data, journeyID } = params;
//     try {
//       let url = `${URLs.ADD_ADHOC_VEHICLE}?date=${date}&journeyID=${journeyID}`;
//       const response = await axiosInstance({
//         method: 'POST',
//         url: url,
//         data: data
//       });
//       thunkApi.dispatch(
//         updateToast({
//           show: true,
//           message: response?.data?.message,
//           severity: 'success'
//         })
//       );
//       return response?.data;
//     } catch (error: any) {
//       return thunkApi.rejectWithValue(error);
//     }
//   }
// );

// //update adhoc vehicle slice
// const updateAdhocVehicleSlice = createSlice({
//   name: 'updateadhocvehicle',
//   initialState,
//   reducers: {},
//   extraReducers: builder => {
//     builder
//       .addCase(updateAdhocVehicle.pending, state => {
//         state.isLoading = true;
//         state.error = null;
//         state.data = null;
//       })
//       .addCase(
//         updateAdhocVehicle.fulfilled,
//         (state, action: PayloadAction<ReturnedType>) => {
//           state.isLoading = false;
//           state.data = action.payload;
//           state.error = null;
//         }
//       )
//       .addCase(updateAdhocVehicle.rejected, (state, action: any) => {
//         state.isLoading = false;
//         state.error = action.payload?.response?.data?.error;
//       });
//   }
// });

export default autoPlannerTripsSlice.reducer;
export const autoPlannerAgentSlice = autoPlannerAgent.reducer;
export const validateContact = conatctValidationSlice.reducer;
export const autoPlannerRouteSlice = autoPlannerRoutes.reducer;
export const addAutoPlannerTripSlice = addAutoPlannerTrip.reducer;
export const GRPTrip = grpbookingSlice.reducer;
export const allBookings = allbookingSlice.reducer;
export const addAutoPlannerBookingSlice = autoPlannerBookingSlice.reducer;
export const upadateAutoPlannerTripSlice = upadateAutoPlannerTrip.reducer;
export const deleteAutoPlannerSliceSlice = deleteAutoPlannerSlice.reducer;
export const importAutoPlannerTripSlice = importAutoPlannerTrip.reducer;
export const getAdhocVehicleReducer = getAdhocVehicleSlice.reducer;
export const getAdhocTransferVehicleReducer = getAdhocTransferVehicleSlice.reducer;
export const AddAdhocVehicleReducer = AddAdhocVehicleSlice.reducer;
// export const updateAdhocVehicleReducer = updateAdhocVehicleSlice.reducer;

export const newPickupLocation = newPickupLocationSlice.reducer;
export const { clearNewPickup } = importAutoPlannerTrip.actions;
export const { clearBookings } = grpbookingSlice.actions;
export const { clearContactError } = conatctValidationSlice.actions;
export const { clearAdhocTransferVehicle } = getAdhocTransferVehicleSlice.actions;
export const { clearAdhocVehicle } = getAdhocVehicleSlice.actions;
